var Layout    = require("enyo-ui/Layout"),
    clipText  = require("app/utils/clipText"),
    dom       = require("enyo/dom"),
    kind      = require("enyo/kind"),
    themeVars = require("app/theme/variables");

module.exports = kind(/** @lends  app.ui.header.HorizontalDescriptionLayout.prototype */ {

    /**
     * @private
     */
    name: "app.ui.header.HorizontalDescriptionLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * Default options for the layout.
     *
     * @type {Object}
     * @protected
     */
    options: /** @lends  app.ui.header.HorizontalDescriptionLayout.prototype */ {

        /**
         * The alignment of the description.
         *
         * Available options:
         *  - `top`
         *  - `middle`
         *  - `bottom`
         *
         * @returns  {string}  The alignment of the description
         * @default  "top"
         * @public
         */
        headerDescriptionAlignment: function() {
            return this.container.get("headerDescriptionAlignment") || "top";
        }
    },

    /**
     * @public
     */
    reflow: function() {
        var $container        = this.container,
            $contentContainer = $container.$.contentContainer,
            $titleWrapper     = $container.$.titleWrapper,
            $secondaryTitle   = $container.$.secondaryTitle,
            $buttonBar        = $container.$.buttonBar,
            $body             = $container.$.body,
            $readMore         = $container.$.readMore,
            $metadata         = $container.$.metadata,
            $ctaBar           = $container.$.ctaBar,
            showReadMore      = false,
            containerBounds   = $container.getAbsoluteBounds(),
            maxContentHeight,
            overflow,
            bodyHeight,
            bodyLineHeight,
            maxBodyHeight,
            contentContainerTop;

        if (!$container.hasNode()) {
            return;
        }

        // Reset
        // --------------------

        // Hide controls but make sure they have their natural dimensions
        this.visuallyHideControl($titleWrapper);
        this.visuallyHideControl($secondaryTitle);
        this.visuallyHideControl($buttonBar);
        this.visuallyHideControl($body);
        this.visuallyHideControl($metadata);
        this.visuallyHideControl($ctaBar);

        // Unclip everything
        this.unclipBody();
        this.unclipMetadata();
        this.unclipSecondaryTitle();
        this.unclipPrimaryTitle();

        // Disable compact mode
        while ($contentContainer.hasClass("compact")) {
            $contentContainer.removeClass("compact");
        }

        // Hide readmore button for now
        this.hideControl($readMore);

        // Clipping
        // --------------------

        // Available height
        maxContentHeight = containerBounds.height - themeVars.getContentPaddingVerticalPx() * 4;

        if (themeVars.appHeaderFloat === true) {
            maxContentHeight -= this.getHeaderRegionHeight();
        }

        // Do we need to crop the body?
        // Hard maximum of 10 lines. Shrink to less than 10 lines, but no fewer
        // than 2 lines if needed to reduce overflow.
        bodyHeight     = this.getControlHeight($body);
        overflow       = this.getContentOverflow(maxContentHeight);
        bodyLineHeight = this.getControlLineHeight($body);
        maxBodyHeight  = Math.min(
            bodyLineHeight * 10,
            Math.max(bodyHeight - overflow, bodyLineHeight * 2)
        );

        if (maxBodyHeight < bodyHeight) {
            if (!showReadMore) {
                showReadMore = true;
                this.visuallyHideControl($readMore);
                overflow     += this.getControlHeight($readMore);
            }

            this.clipBody(maxBodyHeight);
        }

        // Do we need to crop the metadata?
        overflow = this.getContentOverflow(maxContentHeight);

        if (overflow > 0) {
            if (!showReadMore) {
                showReadMore = true;
                this.visuallyHideControl($readMore);
                overflow     += this.getControlHeight($readMore);
            }
            this.clipMetadata(overflow);
        }

        // Do we need to go for a compact spacing?
        overflow = this.getContentOverflow(maxContentHeight);

        if (overflow > 0) {
            if (!$contentContainer.hasClass("compact")) {
                $contentContainer.addClass("compact");
            }
        }

        // Do we need to crop the secondaryTitle?
        overflow = this.getContentOverflow(maxContentHeight);

        if (overflow > 0) {
            if (!showReadMore) {
                showReadMore = true;
                this.visuallyHideControl($readMore);
                overflow     += this.getControlHeight($readMore);
            }

            this.clipSecondaryTitle(overflow);
        }

        // Do we need to crop the secondaryTitle?
        overflow = this.getContentOverflow(maxContentHeight);

        if (overflow > 0) {
            if (!showReadMore) {
                showReadMore = true;
                this.visuallyHideControl($readMore);
                overflow     += this.getControlHeight($readMore);
            }

            this.clipPrimaryTitle(overflow);
        }

        // Alignment
        // --------------------

        switch (this.getOption("headerDescriptionAlignment")) {
            case "top":
                contentContainerTop = themeVars.getContentPaddingVerticalPx() * 2;

                if (themeVars.appHeaderFloat === true) {
                    contentContainerTop += this.getHeaderRegionHeight();
                }

                $contentContainer.addStyles({
                    top:    contentContainerTop + "px",
                    bottom: "auto"
                });
                dom.transformValue($contentContainer, "translateY", null);
                break;
            case "bottom":
                $contentContainer.addStyles({
                    top:    "auto",
                    bottom: (themeVars.getContentPaddingVerticalPx() * 2) + "px"
                });
                dom.transformValue($contentContainer, "translateY", null);
                break;
            case "middle":
            default:
                contentContainerTop = themeVars.getContentPaddingVerticalPx() * 2;

                if (themeVars.appHeaderFloat === true) {
                    contentContainerTop += this.getHeaderRegionHeight();
                }

                contentContainerTop += maxContentHeight / 2;

                $contentContainer.addStyles({
                    top:    contentContainerTop + "px",
                    bottom: "auto"
                });
                dom.transformValue($contentContainer, "translateY", "-50%");
                break;
        }

        // Reveal result
        // --------------------

        this.showControl($titleWrapper);

        if (($secondaryTitle.hasNode() || {offsetHeight: 0}).offsetHeight) {
            this.showControl($secondaryTitle);
        }
        else {
            this.hideControl($secondaryTitle);
        }

        if (($buttonBar.hasNode() || {offsetHeight: 0}).offsetHeight) {
            this.showControl($buttonBar);
        }
        else {
            this.hideControl($buttonBar);
        }

        if (($body.hasNode() || {offsetHeight: 0}).offsetHeight) {
            this.showControl($body);
        }
        else {
            this.hideControl($body);
        }

        if (($metadata.hasNode() || {offsetHeight: 0}).offsetHeight) {
            this.showControl($metadata);
        }
        else {
            this.hideControl($metadata);
        }

        showReadMore && this.showControl($readMore);

        if (($ctaBar.hasNode() || {offsetHeight: 0}).offsetHeight) {
            this.showControl($ctaBar);
        }
        else {
            this.hideControl($ctaBar);
        }
    },

    // Dimensions
    // --------------------

    /**
     * Get the height of the headerRegion.
     *
     * @return {number} The height
     */
    getHeaderRegionHeight: function() {
        var appView            = this.container.app.view,
            headerRegionBounds = appView.get("headerRegionBounds") || {};

        return headerRegionBounds.height || 0;
    },

    /**
     * Get the height of the control
     *
     * @param    {enyo.Control}  control  The control
     * @returns  {number}   The control height in pixels
     * @private
     */
    getControlHeight: function(control) {
        return (control.hasNode() || {offsetHeight: 0}).offsetHeight;
    },

    /**
     * Gets the current amount of content overflow.
     *
     * @param    {number}  maxContentHeight  The maximum content height
     * @returns  {number}  The overflow height in pixels
     * @private
     */
    getContentOverflow: function(maxContentHeight) {
        var $container        = this.container,
            $contentContainer = $container.$.contentContainer,
            contentHeight,
            overflow;

        contentHeight = this.getControlHeight($contentContainer);
        overflow      = contentHeight - maxContentHeight;

        return Math.max(0, overflow);
    },

    /**
     * Get the line height of the body in pixels.
     *
     * @param    {enyo.Control}  control  The control
     * @returns  {number}   The line height in pixels
     * @private
     */
    getControlLineHeight: function(control) {
        var node = control.hasNode();

        return parseFloat(dom.getComputedStyleValue(node, "line-height"), 10);
    },

    // Clipping
    // --------------------

    /**
     * Clip the body.
     *
     * @param  {number}  maxBodyHeight  The max height
     * @private
     */
    clipBody: function(maxBodyHeight) {
        var $container = this.container,
            $body      = $container.$.body,
            bodyNode   = $body.hasNode();

        clipText.clip(bodyNode, maxBodyHeight, true);
    },

    /**
     * Clip metadata, one item at a time.
     *
     * @param  {number}  overflow  The amount of overflow we want to reduce
     * @private
     */
    clipMetadata: function(overflow) {
        var $container         = this.container,
            $metadata          = $container.$.metadata,
            records            = $metadata.get("data") || [],
            length             = records.length,
            i,
            index,
            $item,
            eliminatedOverflow = 0;

        // Hide items, starting at the bottom, until overflow has been
        // eliminated or no more items are available.
        for (i = length - 1; i >= 0; i--) {
            index = $metadata.getIndexByRecord(records[i]);
            $item = $metadata.getChildForIndex(index);

            if ($item) {
                eliminatedOverflow += this.getControlHeight($item);
                $item.hide();
            }

            if (eliminatedOverflow >= overflow) {
                break;
            }
        }
    },

    /**
     * Clip secondary title.
     *
     * @param  {number}  overflow  The amount of overflow we want to reduce
     * @private
     */
    clipSecondaryTitle: function(overflow) {
        var $container      = this.container,
            $secondaryTitle = $container.$.secondaryTitle,
            height          = this.getControlHeight($secondaryTitle),
            lineHeight      = this.getControlLineHeight($secondaryTitle),
            lines;

        lines = Math.max(
            0,
            Math.floor((height - overflow) / lineHeight)
        );

        if (!$secondaryTitle.hasClass("clip")) {
            $secondaryTitle.addClass("clip");
            lines > 0
                ? $secondaryTitle.applyStyle("-webkit-line-clamp", lines)
                : $secondaryTitle.applyStyle("height", "0px");
        }
    },

    /**
     * Clip primary title.
     *
     * @param  {number}  overflow  The amount of overflow we want to reduce
     * @private
     */
    clipPrimaryTitle: function(overflow) {
        var $container = this.container,
            $title     = $container.$.title,
            height     = this.getControlHeight($title),
            lineHeight = this.getControlLineHeight($title),
            lines;

        lines = Math.max(
            1,
            Math.floor((height - overflow) / lineHeight)
        );

        if (!$title.hasClass("clip")) {
            $title.addClass("clip");
            $title.applyStyle("-webkit-line-clamp", lines);
        }
    },

    // Unclipping
    // --------------------

    /**
     * Unclip the description text
     */
    unclipBody: function() {
        var $container = this.container,
            $body      = $container.$.body,
            node       = $body.hasNode();

        clipText.unclip(node);
    },

    /**
     * Unclip the metadata
     */
    unclipMetadata: function() {
        var $container = this.container,
            $metadata  = $container.$.metadata,
            records    = $metadata.get("data") || [];

        records.forEach(function(record) {
            var index,
                $item;

            index = $metadata.getIndexByRecord(record);
            $item = $metadata.getChildForIndex(index);
            $item && $item.show();
        });
    },

    /**
     * Unclip secondary title
     */
    unclipSecondaryTitle: function() {
        var $container      = this.container,
            $secondaryTitle = $container.$.secondaryTitle;

        $secondaryTitle.applyStyle("-webkit-line-clamp", null);
        $secondaryTitle.applyStyle("height", null);

        while ($secondaryTitle.hasClass("clip")) {
            $secondaryTitle.removeClass("clip");
        }
    },

    /**
     * Unclip primary title
     */
    unclipPrimaryTitle: function() {
        var $container = this.container,
            $title     = $container.$.title;

        $title.applyStyle("-webkit-line-clamp", null);
        $title.applyStyle("height", null);

        while ($title.hasClass("clip")) {
            $title.removeClass("clip");
        }
    },

    // Control Visibility
    // --------------------

    /**
     * Fully hides the control
     *
     * @param  {enyo.Control}  control  The control
     * @private
     */
    hideControl: function(control) {
        control.hide();
    },

    /**
     * Ensures the control is displayed in the DOM. It might still be visually
     * hidden, but it will have a working box-model.
     *
     * @param  {enyo.Control}  control  The control
     * @private
     */
    visuallyHideControl: function(control) {
        // Sometimes Chrome doesn't properly update dimensions when using
        // visibility hidden, so we make sure the control is just *slightly*
        // visible. This forces Chrome to calculate it's properties correctly.
        control.applyStyle("opacity", 0.001);
        control.show();
    },

    /**
     * Fully reveals the control
     *
     * @param  {enyo.Control}  control  The control
     * @private
     */
    showControl: function(control) {
        control.applyStyle("opacity", 1);
        control.show();
    }

});
